// dashboard/patientList/StatusNarrative.js
// Shared between ConditionRow and PatientDetailPanel. Extracted from App.js
// (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { c, severityColor, severityBg, severityLabel } from '../constants';
import { relDate, trendDirectionLabel } from '../utils';
import { buildFindingsNarrative } from '../buildFindingsNarrative';

export default function StatusNarrative({ survey, vnextRun, alert, sev, clinicalSummary, recoveryThreshold }) {
  const { t } = useTranslation();
  if (!survey) return <span style={{ fontSize: 12, color: c.textSoft, fontStyle: 'italic' }}>{t('patientCard.noCheckInsYet')}</span>;
  const checkin = relDate(survey.submitted_at);
  // Release-blocker #1 (docs/BUILD_STATE.md, 2026-09-27, Bryan): no raw
  // numeric score anywhere client-facing. This line used to show
  // "Score 57 (+11) · 3 days ago" (patientCard.scoreUp/scoreDown/
  // scoreUnchanged, interpolating {score}/{delta} directly) — replaced
  // with trendDirectionLabel's qualitative word only.
  const direction = survey.clinical_score_delta != null ? trendDirectionLabel(survey.clinical_score_delta, t) : null;

  // Release-blocker #2 (docs/BUILD_STATE.md, 2026-09-27, Bryan): "much
  // better explanations... the VNext architecture... can provide a 2-3
  // line status" naming the SPECIFIC things that changed, instead of a
  // generic severity-band message. vnextRun is survey_evaluation_runs'
  // shadow-engine findings/clusters for this exact submission (App.js/
  // PatientDetailPanel.js) — buildFindingsNarrative turns them into up to
  // 3 plain-English lines, ordered by severity. Preferred over
  // clinicalSummary/alert.message whenever it has content, since it's
  // exactly the specific "why" those were never able to provide; falls
  // back to the older, coarser narratives for conditions/submissions
  // vNext hasn't evaluated yet.
  const findingsLines = buildFindingsNarrative(vnextRun?.findings, vnextRun?.clusters);

  return (
    <div style={{ lineHeight: 1.5 }}>
      {findingsLines.length > 0
        ? <div style={{ fontSize: 12, color: sev === 'red' ? c.red : c.slate, fontWeight: 500 }}>
            {findingsLines.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        : clinicalSummary
          ? <div style={{ fontSize: 12, color: sev === 'red' ? c.red : c.slate, fontWeight: 500 }}>
              {clinicalSummary}
            </div>
          : alert
            ? <div style={{ fontSize: 12, color: sev === 'red' ? c.red : c.slate, fontWeight: 500 }}>
                {alert.message.replace(/^(Critical response flagged for [^:]+:\s*)/i, '')}
              </div>
            : <div style={{ fontSize: 12, color: c.textSoft }}>{t('patientCard.noSignificantChanges')}</div>
      }
      <div style={{ fontSize: 11, color: c.textSoft, marginTop: 1 }}>
        {direction ? `${direction} · ` : ''}{checkin}
      </div>
      {/* current_severity (migration 195/196): the latest single check-in's
          own result, shown only when it differs from the follow-up tier
          (sev) above. Found missing 2026-09-08 — Bryan submitted new real
          surveys that correctly updated this in the backend, but with no
          roster-visible signal, the card looked unchanged, leading to a
          manual dismiss that wasn't actually necessary. This is the fix:
          make the improvement visible on the card itself, not only in the
          detail panel, so a clinician can see recovery progress without
          being tempted to dismiss based on incomplete roster info. */}
      {alert?.current_severity && alert.current_severity !== sev && (
        <div style={{
          fontSize: 11, fontWeight: 700, color: severityColor(alert.current_severity),
          backgroundColor: severityBg(alert.current_severity),
          border: `1px solid ${severityColor(alert.current_severity)}30`,
          borderRadius: 6, padding: '2px 6px', marginTop: 4, display: 'inline-block',
        }}>
          {t('patientCard.latestCheckIn')}: {severityLabel(alert.current_severity)}
        </div>
      )}
      {/* #9 (2026-09-09): recovery_streak was already fetched but never
          rendered anywhere — a clinician had no visibility into how close
          an episode was to stepping down, the exact gap that led to an
          unnecessary manual dismiss on 2026-09-08 (see the comment above).
          Only shown once real progress exists (recovery_streak > 0) — a
          freshly-triggered alert always starts at 0/M, which isn't
          informative enough to show on every single card. recoveryThreshold
          comes from condition_alert_profiles (App.js), the same source
          apply_survey_processing_result_unordered itself reads, so this
          can never drift from what the backend actually gates on. For
          Immediate Review specifically, recovery_streak can sit AT
          threshold indefinitely (migration 197/209's step-down gate caps
          it there until a clinician marks the alert reviewed) — shown as
          its own distinct message rather than a misleading "M of M" that
          looks like it should have already stepped down. */}
      {alert && sev !== 'green' && alert.recovery_streak > 0 && recoveryThreshold != null && (
        <div style={{ fontSize: 11, color: c.textSoft, marginTop: 4 }}>
          {sev === 'immediate_review' && alert.recovery_streak >= recoveryThreshold
            ? t('patientCard.recoveryReadyForReview')
            : t('patientCard.recoveryProgress', { streak: alert.recovery_streak, threshold: recoveryThreshold })}
        </div>
      )}
    </div>
  );
}
